/**
 * Coming back to the card you left from.
 *
 * Two things take a reader off the grid and both have to land them back on the same card: the
 * name on a card, which opens a profile and comes back through the history, and the photograph,
 * which opens the viewer and comes back the moment it closes — usually on somebody else's card,
 * because the viewer walks the feed.
 *
 * The anchor is a **card**, never an offset. `.ee-card-slot` carries `content-visibility: auto`
 * with a `contain-intrinsic-size` guess, so every off-screen height is an estimate until the slot
 * renders and a saved `scrollY` means something different by the time it is restored. A card is a
 * real element with a real id on it, and it stays the same card whatever the page around it does.
 */

/** The card to come back to, and the screen it was left from. */
export interface GridAnchor {
  /** The tapped card's listing id, already on the DOM as `data-ee-listing`. */
  readonly listing_id: string;
  /**
   * The history entry the card was tapped on: `location.key`.
   *
   * The entry, not the path and not the filters. `toQuery({})` on an unnarrowed `/browse` reached
   * twice (from the rail and from the menu) is the identical string, which is exactly why `stores/search.ts` keeps the loaded
   * pages across the round trip — and exactly why matching on filters would fire one entry's anchor
   * on the other. A path match would miss it too: a reader who taps "All Australia"
   * in the drawer has *pushed* a new entry asking for the list, not come back to where they were,
   * and yanking them eighty cards down is the treadmill in reverse.
   *
   * An entry key is the identity of a screen you were on rather than a screen that looks like it.
   * It is stable across back and forward, unique per push, and is what react-router's own
   * `ScrollRestoration` keys on.
   */
  readonly key: string;
  /**
   * The numbered page the card is on. Usually the page the address names, but a reader walking
   * profiles with a swipe can cross into the next page of the list, and the grid they go back to
   * has to show the page their card is on rather than the page they first left.
   */
  readonly page: number;
  /** The random gallery's card is a photograph as well as a listing: `data-ee-photo`. */
  readonly photo_id?: string;
}

/**
 * The entry a grid stands for. The same as `location.key` except straight after the grid has
 * rewritten its own address to the page a profile walk ended on: that rewrite is a navigation of
 * its own, with a new key, so it carries the old one in its state and the anchor still matches.
 */
export function anchorKey(location: { key: string; state: unknown }): string {
  const state = location.state as { anchorKey?: unknown } | null;
  return typeof state?.anchorKey === 'string' ? state.anchorKey : location.key;
}

/** Two frames at the same offset is settled. One is not: the first reading has nothing to match. */
export const STABLE_FRAMES = 2;

/**
 * The give-up, counted in frames rather than milliseconds, because what is being waited for is a
 * layout pass and not an interval. A backgrounded tab stops running `requestAnimationFrame`
 * altogether and picks the count up where it left off, which is the behaviour we want and the one
 * a timer would not give.
 */
export const MAX_FRAMES = 24;

export interface Settle {
  readonly previous: number;
  readonly stable: number;
  readonly frames: number;
  readonly done: boolean;
}

/** `previous: -1` is a sentinel no real offset can equal, so the first frame never counts as stable. */
export const beginSettle = (): Settle => ({ previous: -1, stable: 0, frames: 0, done: false });

/**
 * One frame of the settle. Two consecutive identical offsets mean the page stopped moving under
 * the scroll — which is only measurable because the scroll is instant: an instant `scrollIntoView`
 * updates `scrollY` synchronously, where a smooth one is still animating and never reads the same
 * twice.
 */
export function nextSettle(state: Settle, scrollY: number): Settle {
  const frames = state.frames + 1;
  const stable = scrollY === state.previous ? state.stable + 1 : 0;
  return {
    previous: scrollY,
    stable,
    frames,
    done: stable >= STABLE_FRAMES || frames >= MAX_FRAMES,
  };
}

/** Whether this anchor belongs to the screen being rendered. Narrows, so the caller gets it free. */
export function shouldRestore(anchor: GridAnchor | null, key: string): anchor is GridAnchor {
  return anchor !== null && anchor.key === key;
}

/**
 * `data-ee-listing` is already on every `.ee-card`, so the grid needs no second attribute and no
 * registry of several hundred refs to find one. It now has two readers — the delegated analytics
 * listener and this — which is a fact the brief records rather than a coincidence.
 *
 * Listing ids are `crypto.randomUUID()`, so the escaping can never actually fire. It is here so
 * that an id which one day comes from somewhere else cannot break the selector, and it is written
 * by hand because `CSS.escape` does not exist in the node environment the client tests run in.
 */
export function cardSelector(listing_id: string, photo_id?: string): string {
  const escape = (value: string) => value.replace(/["\\]/g, '\\$&');
  // A random-gallery card is one photograph, and one listing can stand behind several of them.
  const photo = photo_id ? `[data-ee-photo="${escape(photo_id)}"]` : '';
  return `[data-ee-listing="${escape(listing_id)}"]${photo}`;
}

/** A wheel, a finger or a key means the reader has taken over, and the position is theirs now. */
const INTERRUPTS = ['wheel', 'touchstart', 'keydown'] as const;

/**
 * Put a card on screen and keep it there while the page settles under it.
 *
 * The jump has to repeat. A route change destroys every slot, and with it every height the
 * browser had measured, so on the way back each one is on its `contain-intrinsic-size` guess
 * again — the target moves as it is approached. Slots above it never become relevant and never
 * grow, so the error is a couple of screenfuls rather than the whole list, and each correction is
 * smaller than the last.
 *
 * Instant at every motion preference, and not because animation is unwelcome: a `scrollIntoView`
 * re-issued each frame cancels and restarts an in-flight smooth scroll, so it would never arrive,
 * and `scrollY` would never hold still long enough for the loop to know it had finished. There is
 * no animation here to collapse under `prefers-reduced-motion`.
 *
 * A card near the end of a short document clamps at the maximum offset, which reads as settled on
 * the second frame. That is the right answer, not a failure to reach it.
 *
 * Returns a cancel, or `null` when the card is not in the DOM at all — the list it belongs to has
 * moved on, and a wrong scroll is worse than none. `onSettled` fires only when the loop finishes
 * of its own accord, so a caller can spend its anchor there rather than on the way in.
 */
export function scrollCardIntoView(
  listing_id: string,
  onSettled?: () => void,
  photo_id?: string,
): (() => void) | null {
  const card = document.querySelector<HTMLElement>(cardSelector(listing_id, photo_id));
  if (!card) return null;

  let settle = beginSettle();
  let frame = 0;
  let live = true;

  function stop() {
    if (!live) return;
    live = false;
    cancelAnimationFrame(frame);
    for (const name of INTERRUPTS) window.removeEventListener(name, stop);
  }

  function step() {
    if (!card) return;
    card.scrollIntoView({ block: 'start', behavior: 'auto' });
    settle = nextSettle(settle, window.scrollY);
    if (settle.done) {
      stop();
      onSettled?.();
      return;
    }
    frame = requestAnimationFrame(step);
  }

  for (const name of INTERRUPTS) window.addEventListener(name, stop, { passive: true });
  step();
  return stop;
}

/**
 * Take the scroll position off the browser for navigations, and hand it back for reloads.
 *
 * Left on `auto`, the browser answers the same question a second time on a back — from its own
 * record, while the page it recorded is still unmounting — and it can land after the card jump
 * and undo it. Two mechanisms racing for one scroll position is the bug this whole file exists to
 * fix, so only one of them may hold it.
 *
 * The `pagehide` handback is deliberate and its cost is stated rather than hidden: a reload of a
 * long prose page keeps the reader's place, and a reload of a deep-scrolled grid does not deserve
 * to. The feed lives in memory, so a reload starts again at page one, and restoring a twenty-page
 * offset onto a one-page document clamps the reader to the bottom of it.
 */
export function claimScrollRestoration(): void {
  if (!('scrollRestoration' in history)) return;
  history.scrollRestoration = 'manual';
  window.addEventListener('pagehide', () => {
    history.scrollRestoration = 'auto';
  });
  // A page that comes back out of the back-forward cache resumes with whatever `pagehide` left,
  // which is `auto`, and the race above is back on for every in-app navigation after it. Safari
  // and Firefox restore from that cache routinely, so the claim is re-made on the way back in.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) history.scrollRestoration = 'manual';
  });
}
